/**
 * Canonicalls — path copy. Archive URLs only; no substrate branding in prose.
 */

export const CANONICALLS_CONTINUITY_ANCHOR =
  "Representations may drift. The canonical record remains inspectable." as const;

export const CANONICALLS_DRIFT_INSIGHT = CANONICALLS_CONTINUITY_ANCHOR;

/** Muted rail hints — one line per spine step. */
export const CANONICALLS_TRAVERSAL_PHASE_HINT = {
  origin: "Preserve the canonical source.",
  representation: "Representations may drift.",
  verification: "Continuity can be inspected.",
  replay: "The source returns through continuity memory.",
  actor: "Authorship persists across surfaces.",
  agreement: "Obligations attach to the same lineage.",
  lineage: "Continuity stays attached to source lineage.",
} as const;

export const CANONICALLS_SOURCE_PRINCIPLE =
  "The canonical record is the source. Everything else is a representation." as const;

export const CANONICALLS_REPRESENTATIONS_INSIGHT =
  "Posts, sites, exports, mirrors, and summaries are representations — linked to one source." as const;

export const CANONICALLS_SOURCE_VS_REP =
  `${CANONICALLS_SOURCE_PRINCIPLE} ${CANONICALLS_REPRESENTATIONS_INSIGHT}` as const;

export const CANONICALLS_TAGLINE =
  "Canonicalize once. Represent everywhere. Inspect the source." as const;

/** Path 1 — landing */
export const CANONICALLS_HERO = {
  title: "Canonical source continuity",
  subtitle: "Preserve one source. Publish representations. Inspect provenance anytime.",
  carryIntro: "The same canonical object below moves through Origin → Represent → Verify → Replay.",
} as const;

export const CANONICALLS_LANDING = {
  ctaPrimary: "Create canonical source",
  ctaSecondary: "Inspect authenticity",
} as const;

/** Path 2 — canonicalize */
export const CANONICALLS_CANONICALIZE = {
  title: "Preserve canonical source",
  line: "Write the source once. Representations may diverge; the canonical record stays inspectable.",
  composeTitle: "Canonical source",
  composeHint: "This text becomes the source — platforms receive representations only.",
  titleLabel: "Title (optional)",
  authorLabel: "Authorship",
  bodyLabel: "Source text",
  bodyPlaceholder: "The canonical record — what you want preserved and inspectable later.",
  continueLabel: "Review source",
  confirmTitle: "Confirm source",
  confirmHint: "You are preserving the canonical record, not a post preview.",
  confirmCheckbox: "I confirm this as the canonical source.",
  preserveLabel: "Preserve source",
  preserving: "Preserving canonical source…",
  preserved: "Source continuity confirmed. Carried across this traversal.",
  representationTitle: "Representations",
  representationHint: "Previews only — the source above remains authoritative.",
  representationDriftNote: "This representation contains only part of the preserved source.",
  representationCompareSource: "Preserved source",
  representationCompareDrift: "Platform representation",
  replayPartialLabel: "Representation carried (partial)",
  replayRecoveredNote: "The canonical source above restores full context.",
  sourceLinkLabel: "Canonical source link",
  inspectNext: "Continue to verification",
  replayNext: "Replay source continuity",
  assistHint: "Optional suggestion — you confirm the final source text.",
} as const;

export const CANONICALLS_CANONICALIZE_LINE = CANONICALLS_CANONICALIZE.line;

/** Path 3 — inspect */
export const CANONICALLS_INSPECT = {
  title: "Inspect authenticity",
  lead: "Check whether a representation still matches the canonical source.",
  referenceLabel: "Canonical link or lineage reference",
  referenceHint: "From a preserved source, signed export, or continuity archive handoff",
  matchNote: "Confirmed continuity means the representation still traces to the source.",
  handoffToReplay:
    "Inspectable provenance — the same source continues in Memory after drift or deletion.",
  handoffLink: "Replay source continuity",
} as const;

export const CANONICALLS_VERIFY_CARD = {
  inspectTitle: CANONICALLS_INSPECT.title,
  inspectLead: CANONICALLS_INSPECT.lead,
  signed: "Source continuity confirmed",
  snapshot: "Source continuity confirmed",
  evidenceConfirmed: "Evidence continuity confirmed",
  provenanceSigned: "Lineage available in the continuity archive",
  provenanceLocal: "Preserved for inspection on this traversal",
} as const;

export const CANONICALLS_HANDOFF = {
  replayContinuity: "Replay source continuity",
  continueToReplay: "Continue to Replay",
  inspectArchive: "Inspect archive",
  openExternalArchive: "Open external archive",
  externalArchiveNote: "Archive opens in the XO verification layer — outside Canonicalls.",
  returnToSource: "Return to source",
  inspectLineage: "Inspect lineage",
  backToRepresentations: "Back to representations",
} as const;

export const CANONICALLS_BRIDGE = {
  openExternalArchive: CANONICALLS_HANDOFF.openExternalArchive,
  /** @deprecated use openExternalArchive */
  openArchive: CANONICALLS_HANDOFF.openExternalArchive,
  archiveTransition: CANONICALLS_HANDOFF.externalArchiveNote,
  returnHint: "Return to Canonicalls — the same source stays in the card above.",
  footerVerify: "Optional external archive view for signed sources.",
  archiveLabel: "External archive",
} as const;

export const CANONICALLS_FOOTER_VERIFY = CANONICALLS_BRIDGE.footerVerify;
export const CANONICALLS_FOOTER_ARCHIVE_LABEL = CANONICALLS_BRIDGE.archiveLabel;

/** Path 4 — replay */
export const CANONICALLS_REPLAY = {
  title: "Continuity memory",
  lead: "Recovered canonical source from continuity memory.",
  empty: "No canonical source in continuity memory yet.",
  emptyCarry: "Recovered canonical source from continuity memory will appear after preservation.",
  preservedAt: "Preserved",
  carriedForward: "Carried forward",
  representationLabel: "Representation preview (partial)",
  openRecord: "Open source",
  externalArchiveNote: CANONICALLS_HANDOFF.externalArchiveNote,
  timelineLabel: "Provenance timeline",
  handoffFromVerify: "The source returns through continuity memory.",
  rememberedLine: "The inspected source is remembered on this traversal.",
  provenanceReturn: "Authorship and lineage return to the canonical source.",
} as const;

/** Path 5 — actor */
export const CANONICALLS_AETHER = {
  title: "Actor continuity",
  lead: "Authorship and destinations persist across surfaces — not a profile or wallet.",
  identityTitle: "Continuity identity",
  identityBody:
    "Actor continuity binds authorship, provenance, and destinations to one lineage.",
  driftLine: "When platforms change, actor continuity keeps authorship inspectable on the same source.",
  destinationsLabel: "Verified destinations",
  agreementTitle: "Agreement continuity",
  agreementBody: "Obligations and destinations inherit the same lineage as the canonical source.",
  carriedActor: "Carried authorship",
  diagram: {
    actor: "Actor",
    representations: "Representations",
    destinations: "Destinations",
  },
  binds: [
    "Authorship survives platform drift",
    "Representations link back to source",
    "Destinations verify on the lineage",
    "Agreements attach as signed exports",
  ],
} as const;

export const CANONICALLS_AGREEMENT = {
  exportBody:
    "Signed exports and inspectable obligations — institutional continuity, not payment rails.",
  items: [
    "Exports bound to canonical lineage",
    "Obligations inspectable with representations",
    "Destinations verified on actor continuity",
    "Agreement lineage returns to source",
  ],
} as const;

/** Path 6–7 — traversal & archive bridge */
export const CANONICALLS_PATH_BRIDGE = {
  inspect: "Inspect",
  memory: "Memory",
  actor: "Actor",
  source: "Source",
  lineage: "Lineage",
  agreement: "Agreement",
} as const;

export const CANONICALLS_EVIDENCE = {
  attachEvidence: "Attach evidence",
  evidenceOnRecord: "Evidence on record",
} as const;

export const CANONICALLS_SOURCE_CARD = {
  digestLabel: "Digest",
  stateLead: {
    origin: "Compose and preserve one record — representations follow the source.",
    represent: "Platform previews only; the canonical object above stays authoritative.",
    verify: "Continuity can be inspected against this source.",
    replay: "The source returns through continuity memory.",
  },
  excerptFallback: {
    origin: "Write source text below — one record carries through the traversal.",
    represent: "Canonical source carried across traversal.",
    verify: "Source continuity confirmed across traversal.",
    replay: "Recovered canonical source from continuity memory.",
  },
  originEmpty: "Compose below — this object persists through Origin → Represent → Verify → Replay.",
  replayMemory: "Recovered canonical source from continuity memory.",
  verifyMatch: "Source continuity confirmed — representation traces to this record.",
  verifyPending: "Continuity can be inspected — enter a reference or open the archive.",
} as const;

export const CANONICALLS_CAPSULE = {
  evidenceAttached: (count: number) =>
    count === 1 ? "1 evidence attached" : `${count} evidence attached`,
  digestPrefix: "Digest",
  label: "Traversal",
  supportHint: "Same canonical object in view below",
  empty: "No carried source yet",
  memoryLink: "Memory",
  inspectHandoff: "Inspectable → Memory",
  replayRemembered: "Verified · remembered",
  preserveLink: "Preserve source",
} as const;

export const CANONICALLS_CONTINUITY_MEMORY = {
  title: "Continuity memory",
  lead: "The source, authorship, and lineage persist on this traversal.",
  inspectLead: "Same source — inspection hands off to memory on the rail above.",
  replayLead: "Verified continuity returning after drift — same source, later traversal.",
} as const;

export const CANONICALLS_ECHO_LINES = [
  CANONICALLS_CONTINUITY_ANCHOR,
  "Lineage returns to the canonical source.",
] as const;

export const CANONICALLS_AMBIENT_CAPTURE = [
  "Browser · preserve at source",
  "Share sheet · canonicalize",
  "CMS · attach lineage",
  "Institutional publish · signed handoff",
] as const;

export const CANONICALLS_AMBIENT_NOTE =
  "Canonicalization travels with the source. This site is the public clarity layer for inspection and archive." as const;

export const CANONICALLS_LINEAGE_KINDS = [
  { kind: "Source", state: "Canonical", drift: "canonical", hint: "" },
  { kind: "Publication", state: "Representation", drift: "representation", hint: "" },
  { kind: "Mirror", state: "Derived", drift: "derived-mirror", hint: "" },
  { kind: "Summary", state: "Summarized", drift: "summarized", hint: "" },
  { kind: "Export", state: "Exported", drift: "exported", hint: "" },
  { kind: "Agreement", state: "Obligation", drift: "agreement-linked", hint: "" },
] as const;

export type CanonicallsCategoryStatus = "live-mvp" | "partial" | "next" | "future" | "underlying";

export type CanonicallsCategory = {
  id: string;
  title: string;
  status: CanonicallsCategoryStatus;
  statusLabel: string;
  description: string;
};

export const CANONICALLS_CATEGORY_STATUS_LABEL: Record<CanonicallsCategoryStatus, string> = {
  "live-mvp": "Live MVP",
  partial: "Partial",
  next: "Next",
  future: "Future",
  underlying: "Underlying",
};

export const CANONICALLS_CATEGORIES = {
  title: "Continuity categories",
  intro:
    "Canonicalls begins with canonical posts. The same continuity model extends to actors, media, agreements, destinations, and lineage.",
  honestyNote:
    "Status reflects what is available on this surface today — not a delivery promise.",
  items: [
    {
      id: "posts",
      title: "Canonical posts",
      status: "live-mvp",
      statusLabel: "Live MVP",
      description:
        "Public messages and posts become canonical continuity records. Social posts are representations.",
    },
    {
      id: "actors",
      title: "Canonical actors",
      status: "partial",
      statusLabel: "Partial",
      description:
        "Actor continuity connects authorship, handles, verified destinations, and long-term reputation across surfaces.",
    },
    {
      id: "media",
      title: "Canonical media",
      status: "next",
      statusLabel: "Next",
      description:
        "Screenshots, PDFs, mirrors, AI summaries, and exports can remain linked to their canonical source.",
    },
    {
      id: "agreements",
      title: "Canonical agreements",
      status: "next",
      statusLabel: "Next",
      description:
        "Obligations, signatures, partner terms, and institutional commitments can attach to continuity lineage.",
    },
    {
      id: "destinations",
      title: "Canonical destinations",
      status: "future",
      statusLabel: "Future",
      description:
        "Official payment rails, contact routes, websites, and institutional endpoints can be verified as representations of the actor or source.",
    },
    {
      id: "lineage",
      title: "Canonical lineage",
      status: "underlying",
      statusLabel: "Underlying",
      description:
        "Derived mirrors, edits, transforms, summaries, and replays remain inspectable through lineage.",
    },
  ],
} as const satisfies {
  title: string;
  intro: string;
  honestyNote: string;
  items: readonly CanonicallsCategory[];
};

export const CANONICALLS_CATEGORIES_LINK = "Continuity categories" as const;
